import Cookies from 'js-cookie';
import { useAxios } from './useAxios';
import { STORAGE_KEYS } from '@/constants/StorageKeys';
import { computed } from 'vue';
import { Organization } from '@/interfaces/organizations/Organization';

export const betaTesterOrganizationIds = [1];

export interface CreateOrganizationDTO {
    name: string;
    document: string;
    contactEmail: string;
    contactPhone: string;
}

export function useOrganization() {
    const axios = useAxios(process.env.VUE_APP_API_REST_BASE_URL);

    let organizationData = Cookies.get(STORAGE_KEYS.ORGANIZATION);

    if (!organizationData) {
        organizationData = localStorage.getItem(STORAGE_KEYS.ORGANIZATION) || undefined;
    }

    const organization = organizationData
        ? JSON.parse(organizationData)
        : { id: process.env.VUE_APP_ORGANIZATION_ID };

    const create = async (payload: CreateOrganizationDTO): Promise<Organization> => {
        // Remove tudo que não for dígito
        const sanitizedPayload: CreateOrganizationDTO = {
            ...payload,
            document: payload.document.replace(/\D/g, ''),
            contactPhone: payload.contactPhone.replace(/\D/g, ''),
        };

        const response = await axios.post(`/organizations`, sanitizedPayload);

        return response.data;
    };

    const getAll = async () => {
        const response = await axios.get('/organizations');

        return response.data;
    };

    /**
     * Choose an organization to use in the dashboard
     * @param organization The organization to choose
     */
    const chooseOrganization = (organization: Organization) => {
        const isDev = process.env.VUE_APP_ENVIRONMENT === 'development';
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

        let domain = '';
        if (!isDev && !isLocalhost) {
            const hostname = window.location.hostname;
            const parts = hostname.split('.');

            if (parts.length > 2) {
                domain = `domain=.${parts.slice(-2).join('.')};`;
            } else if (parts.length === 2) {
                domain = `domain=.${hostname};`;
            }
        }

        const secure = isDev || isLocalhost ? '' : 'SameSite=None; Secure;';

        // Garante que o valor do cookie está escapado corretamente
        const value = encodeURIComponent(JSON.stringify(organization));

        try {
            document.cookie = `${STORAGE_KEYS.ORGANIZATION}=${value}; path=/; ${domain} max-age=432000; ${secure}`;
            localStorage.setItem(STORAGE_KEYS.ORGANIZATION, JSON.stringify(organization));
        } catch (error) {
            console.error('Erro ao salvar organização:', error);
        }

    };

    const getById = async (id: number) => {
        const organizations = await getAll();
        return organizations.find((o: Organization) => o.id == id);
    };

    const update = async (id: number, payload: any) => {
        const response = await axios.patch(`/organizations/${id}`, payload);
        return response.data;
    };

    const isBetaTester = computed(() => {
        return betaTesterOrganizationIds.includes(organization?.id);
    });

    const getActiveReferral = async () => {
        const response = await axios.get('/organizations/referrals/active');

        return response.data;
    };

    return {
        create,
        organization,
        getAll,
        chooseOrganization,
        isBetaTester,
        getById,
        update,
        getActiveReferral,
    };
}
